import { TextInput } from '@amelio/ui-library';
import { useState } from 'react';

import { SCORE_DECIMAL_PLACES, SCORE_MAX, SCORE_MIN } from '../calibrationConstants';
import { SCORE_INPUT_WIDTH } from '../calibrationStyles';

export interface ScoreInputProps {
    value: number | null;
    onChange: (value: number | null) => void;
    ariaLabel: string;
    dataTestId?: string;
}

const ROUNDING_FACTOR = 10 ** SCORE_DECIMAL_PLACES;

const clampScore = (value: number): number => Math.min(SCORE_MAX, Math.max(SCORE_MIN, value));
const roundScore = (value: number): number => Math.round(value * ROUNDING_FACTOR) / ROUNDING_FACTOR;

const isInScale = (value: number): boolean => value >= SCORE_MIN && value <= SCORE_MAX;

/**
 * Numeric calibrated-score field on the report's 0-100 scale, with one decimal.
 *
 * While the field has focus it shows exactly what was typed, and only reports a value that is
 * already on the scale: clamping on every keystroke would turn a "96" being edited into "996" and
 * then, silently and irreversibly, into "100". Leaving the field settles it — the typed number is
 * clamped and rounded once, and only then reported upward, so the debounced save never sees an
 * off-scale intermediate.
 */
export const ScoreInput = ({ value, onChange, ariaLabel, dataTestId }: ScoreInputProps) => {
    const [draft, setDraft] = useState<string | null>(null);

    const handleChange = (raw: string) => {
        setDraft(raw);

        if (raw.trim() === '') {
            onChange(null);
            return;
        }

        const parsed = Number(raw);
        if (!Number.isNaN(parsed) && isInScale(parsed)) {
            onChange(parsed);
        }
    };

    const handleBlur = () => {
        const raw = draft;
        setDraft(null);

        if (raw === null || raw.trim() === '') {
            return;
        }

        const parsed = Number(raw);
        if (Number.isNaN(parsed)) {
            return;
        }

        const settled = roundScore(clampScore(parsed));
        if (settled !== value) {
            onChange(settled);
        }
    };

    return (
        <div className={SCORE_INPUT_WIDTH}>
            <TextInput
                type={'number'}
                size={'sm'}
                value={draft ?? (value === null ? '' : String(value))}
                onChange={handleChange}
                onBlur={handleBlur}
                ariaLabel={ariaLabel}
                dataTestId={dataTestId}
            />
        </div>
    );
};
